/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { assetImages } from "constants";
import PatientDetailsCard from "../PatientDetailsCard/PatientDetailsCard";

const SaveRespirationRatePopup = ({
  patientObj = null,
  afterActionClose = () => {},
  selectedRespirationRateId = null, // Default value for selectedRespirationRateId
  setSelectedRespirationRateId = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  // States for input fields
  const [respirationRate, setRespirationRate] = useState("");
  const [respirationRateDate, setRespirationRateDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Default to today's date
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading respiration rate details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!respirationRate) {
      newErrors.respirationRate = t("Please enter respiration rate value!");
    } else if (isNaN(respirationRate) || respirationRate <= 0) {
      newErrors.respirationRate = t(
        "Please enter a valid respiration rate number!"
      );
    }
    if (!respirationRateDate) {
      // Validation for new date field
      newErrors.respirationRateDate = t("Please enter a date!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  //function for get respiration rate details
  const getRespirationRateDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_RESPIRATION_BY_ID +
        `/${selectedRespirationRateId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const respirationRateData = response.data;

        setRespirationRate(respirationRateData.rate);
        setRespirationRateDate(
          respirationRateData.date
            ? new Date(respirationRateData.date).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0]
        ); // Set date from API data, default to today's date if no date is available
        setAssignTo(respirationRateData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveRespirationRateHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        rate: parseInt(respirationRate),
        date: respirationRateDate, // Include date in API data
        user: assignTo ? assignTo?.value : null,
      };

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedRespirationRateId) {
        requestUrl +=
          url.API_UPDATE_RESPIRATION +
          `/${selectedRespirationRateId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_RESPIRATION + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#saveRespirationRateOffCanvas")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving respiration rate:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setRespirationRate("");
    setRespirationRateDate(new Date().toISOString().split("T")[0]); // Reset to today's date
    setAssignTo(patientObj);
    setErrors({});
    setSelectedRespirationRateId(null);
  };

  useEffect(() => {
    if (patientObj && !selectedRespirationRateId) {
      setAssignTo(patientObj);
    }
  }, [patientObj, selectedRespirationRateId]);

  useEffect(() => {
    if (selectedRespirationRateId) {
      getRespirationRateDetails();
    }
  }, [selectedRespirationRateId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveRespirationRateOffCanvas"
      aria-labelledby="saveRespirationRateOffCanvas"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              air
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="saveRespirationRateOffCanvas"
            >
              {selectedRespirationRateId
                ? t("Edit Respiration Rate")
                : t("Save Respiration Rate")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedRespirationRateId
                ? t("Manage existing respiration rate details")
                : t("Enter and save respiration rate readings")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading respiration rate details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading respiration rate details...")}
            </p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="d-flex flex-column h-100"
            >
              <div className="fields-container flex-fill">
                {/* Respiration Rate and Date on the same line */}
                <div className="row">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="respirationRate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Respiration Rate (breaths/min) *
                      </label>
                      <input
                        type="number"
                        id="respirationRate"
                        className={`form-control fs-sm shadow-none ${
                          errors.respirationRate
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 16"
                        value={respirationRate}
                        onChange={(e) => {
                          setRespirationRate(e.target.value);
                          if (errors.respirationRate) {
                            setErrors((prev) => ({
                              ...prev,
                              respirationRate: "",
                            }));
                          }
                        }}
                      />
                      {errors.respirationRate && (
                        <div className="invalid-feedback">
                          {errors.respirationRate}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Date input */}
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="respirationRateDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Date *
                      </label>
                      <input
                        type="date"
                        id="respirationRateDate"
                        className={`form-control fs-sm shadow-none ${
                          errors.respirationRateDate
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        value={respirationRateDate}
                        onChange={(e) => {
                          setRespirationRateDate(e.target.value);
                          if (errors.respirationRateDate) {
                            setErrors((prev) => ({
                              ...prev,
                              respirationRateDate: "",
                            }));
                          }
                        }}
                      />
                      {errors.respirationRateDate && (
                        <div className="invalid-feedback">
                          {errors.respirationRateDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assign To field */}
                <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />
                  {assignTo ? <PatientDetailsCard patient={assignTo} /> : null}
                </div>
              </div>

              {/* Respiration Rate Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-symbols-outlined text-primary">
                        assessment
                      </span>
                      Respiration Rate Interpretation
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Reference ranges for adults
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Normal
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Respiration Rate:</strong> 12-20 breaths/min
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined fs-6">info</span>
                    Consult your healthcare provider for proper interpretation
                    and treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveRespirationRateHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Respiration Rate")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveRespirationRatePopup;
